import { useCallback, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { PasswordRulesChecklist } from '../components/auth/PasswordRules';
import { PlatformTileGrid } from '../components/auth/PlatformTileGrid';
import { getCognitoConfig } from '../lib/auth/cognito-config';
import { signUpWithEmailPassword } from '../lib/auth/email-password';
import {
  dateOfBirthError,
  emailError,
  firstNameError,
  lastNameError,
  normalizePhoneE164,
  passwordConfirmError,
  passwordError,
  phoneError,
  usernameError,
} from '../lib/auth/validation';

/**
 * Sign-up surface (round-22 sign-up UX rework).
 *
 * Every field carries onBlur validation — a per-field `touched`
 * flag flips true when the user tabs out of the field, and the
 * corresponding MUI `TextField` renders the validator's error
 * message the moment the user leaves the field. The submit button
 * runs the same validators again to catch fields the user never
 * touched.
 *
 * Optional fields carry no "(optional)" suffix in the label —
 * required fields render MUI's automatic asterisk via `required`,
 * optional fields render bare labels. Password requirements are
 * shown as a live checklist next to the password field so the user
 * sees each rule turn from red X to green ✓ as they type.
 *
 * Preferred platforms render as a multi-select tile grid rather
 * than a dropdown, and phone uses the country-selector input from
 * `mui-tel-input` with live per-country format validation on blur.
 */

interface FormState {
  readonly username: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneE164: string;
  readonly dateOfBirth: string;
  readonly password: string;
  readonly passwordConfirm: string;
  readonly preferredPlatforms: ReadonlyArray<string>;
  readonly optInNewsletter: boolean;
}

const INITIAL_FORM: FormState = {
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneE164: '',
  dateOfBirth: '',
  password: '',
  passwordConfirm: '',
  preferredPlatforms: [],
  optInNewsletter: true,
};

/** Per-field "the user has interacted with this control" flag. */
interface TouchedState {
  username: boolean;
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  phoneE164: boolean;
  dateOfBirth: boolean;
  password: boolean;
  passwordConfirm: boolean;
}

const INITIAL_TOUCHED: TouchedState = {
  username: false,
  firstName: false,
  lastName: false,
  email: false,
  phoneE164: false,
  dateOfBirth: false,
  password: false,
  passwordConfirm: false,
};

/** Pending profile stored in sessionStorage while Cognito verifies the email. */
export interface PendingProfile {
  readonly email: string;
  readonly username: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phoneE164?: string;
  readonly dateOfBirth?: string;
  readonly preferredPlatforms?: ReadonlyArray<string>;
  readonly optInNewsletter: boolean;
  readonly password: string;
}

const PENDING_KEY = 'dvgup-pending-profile';

export function stashPendingProfile(pending: PendingProfile): void {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

export function loadPendingProfile(): PendingProfile | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as PendingProfile;
  } catch {
    return null;
  }
}

export function clearPendingProfile(): void {
  sessionStorage.removeItem(PENDING_KEY);
}

/** Aggregate every validator; return the first error message or undefined. */
function firstFormError(form: FormState): string | undefined {
  return (
    usernameError(form.username) ??
    firstNameError(form.firstName) ??
    lastNameError(form.lastName) ??
    emailError(form.email) ??
    phoneError(form.phoneE164) ??
    dateOfBirthError(form.dateOfBirth) ??
    passwordError(form.password) ??
    passwordConfirmError(form.password, form.passwordConfirm)
  );
}

export default function SignUpPage(): JSX.Element {
  const navigate = useNavigate();
  const { userPoolId, userPoolClientId } = getCognitoConfig();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [touched, setTouched] = useState<TouchedState>(INITIAL_TOUCHED);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const isConfigured = userPoolId !== '' && userPoolClientId !== '';
  const patch = (next: Partial<FormState>): void => setForm({ ...form, ...next });
  const touch = (field: keyof TouchedState): void =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errUsername = usernameError(form.username);
  const errFirst = firstNameError(form.firstName);
  const errLast = lastNameError(form.lastName);
  const errEmail = emailError(form.email);
  const errPhone = phoneError(form.phoneE164);
  const errDob = dateOfBirthError(form.dateOfBirth);
  const errPassword = passwordError(form.password);
  const errPasswordConfirm = passwordConfirmError(form.password, form.passwordConfirm);

  const handleSubmit = useCallback(
    async (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      // Mark every field touched so any lingering error surfaces.
      setTouched({
        username: true,
        firstName: true,
        lastName: true,
        email: true,
        phoneE164: true,
        dateOfBirth: true,
        password: true,
        passwordConfirm: true,
      });
      const validation = firstFormError(form);
      if (validation !== undefined) {
        setSubmitError(validation);
        return;
      }
      setSubmitError(undefined);
      setSubmitting(true);
      try {
        // Cognito's phone_number attribute + our backend's
        // phoneE164 regex both require strict E.164 without
        // whitespace. mui-tel-input emits formatted strings for
        // display; strip formatting here before it hits Cognito.
        const phoneNormalized =
          form.phoneE164.length > 0 ? normalizePhoneE164(form.phoneE164) : undefined;
        await signUpWithEmailPassword({
          userPoolId,
          userPoolClientId,
          email: form.email,
          password: form.password,
          username: form.username,
          firstName: form.firstName,
          lastName: form.lastName,
          phoneE164: phoneNormalized,
        });
        stashPendingProfile({
          email: form.email,
          username: form.username,
          firstName: form.firstName,
          lastName: form.lastName,
          phoneE164: phoneNormalized,
          dateOfBirth: form.dateOfBirth.length > 0 ? form.dateOfBirth : undefined,
          preferredPlatforms:
            form.preferredPlatforms.length > 0 ? form.preferredPlatforms : undefined,
          optInNewsletter: form.optInNewsletter,
          password: form.password,
        });
        navigate('/verify-email');
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'sign-up failed');
      } finally {
        setSubmitting(false);
      }
    },
    [form, navigate, userPoolClientId, userPoolId],
  );

  return (
    <Card sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/sign-up"
          pageTitle="Create account"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
          Create your account
        </Typography>
        {!isConfigured && (
          <Alert severity="info" role="status" sx={{ mb: 2 }}>
            Sign-up temporarily unavailable
          </Alert>
        )}
        {isConfigured && (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Username / gamertag"
                required
                value={form.username}
                onChange={(e) => patch({ username: e.target.value })}
                onBlur={() => touch('username')}
                error={touched.username && errUsername !== undefined}
                helperText={
                  touched.username && errUsername !== undefined
                    ? errUsername
                    : '3-30 characters. Letters, digits, _ or -. Must be unique.'
                }
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="First name"
                  required
                  fullWidth
                  value={form.firstName}
                  onChange={(e) => patch({ firstName: e.target.value })}
                  onBlur={() => touch('firstName')}
                  error={touched.firstName && errFirst !== undefined}
                  helperText={touched.firstName ? errFirst : undefined}
                />
                <TextField
                  label="Last name"
                  required
                  fullWidth
                  value={form.lastName}
                  onChange={(e) => patch({ lastName: e.target.value })}
                  onBlur={() => touch('lastName')}
                  error={touched.lastName && errLast !== undefined}
                  helperText={touched.lastName ? errLast : undefined}
                />
              </Stack>
              <TextField
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={(e) => patch({ email: e.target.value })}
                onBlur={() => touch('email')}
                autoComplete="email"
                error={touched.email && errEmail !== undefined}
                helperText={touched.email ? errEmail : undefined}
              />
              <MuiTelInput
                label="Phone"
                value={form.phoneE164}
                onChange={(value) => patch({ phoneE164: value })}
                onBlur={() => touch('phoneE164')}
                defaultCountry="US"
                preferredCountries={['US', 'CA', 'GB', 'AU']}
                forceCallingCode
                focusOnSelectCountry
                error={touched.phoneE164 && errPhone !== undefined}
                helperText={touched.phoneE164 ? errPhone : undefined}
              />
              <TextField
                label="Date of birth"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => patch({ dateOfBirth: e.target.value })}
                onBlur={() => touch('dateOfBirth')}
                slotProps={{ inputLabel: { shrink: true } }}
                error={touched.dateOfBirth && errDob !== undefined}
                helperText={touched.dateOfBirth ? errDob : undefined}
              />
              <PlatformTileGrid
                label="Preferred platforms"
                value={form.preferredPlatforms}
                onChange={(next) => patch({ preferredPlatforms: next })}
              />
              <Divider />
              <Box>
                <TextField
                  label="Password"
                  type="password"
                  required
                  fullWidth
                  value={form.password}
                  onChange={(e) => patch({ password: e.target.value })}
                  onBlur={() => touch('password')}
                  autoComplete="new-password"
                  error={touched.password && errPassword !== undefined}
                  helperText={
                    touched.password && errPassword !== undefined ? errPassword : undefined
                  }
                />
                <PasswordRulesChecklist value={form.password} />
              </Box>
              <TextField
                label="Confirm password"
                type="password"
                required
                value={form.passwordConfirm}
                onChange={(e) => patch({ passwordConfirm: e.target.value })}
                onBlur={() => touch('passwordConfirm')}
                autoComplete="new-password"
                error={touched.passwordConfirm && errPasswordConfirm !== undefined}
                helperText={touched.passwordConfirm ? errPasswordConfirm : undefined}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.optInNewsletter}
                    onChange={(e) => patch({ optInNewsletter: e.target.checked })}
                  />
                }
                label="Subscribe to the DVGUP newsletter when it launches"
              />
              {submitError && <Alert severity="error">{submitError}</Alert>}
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Creating account…' : 'Create account'}
              </Button>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                Already have an account?{' '}
                <Link component={RouterLink} to="/sign-in">
                  Sign in
                </Link>
              </Typography>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
